/**
 * Global term-to-term search dictionary — maps generic HoReCa-equipment
 * vocabulary a buyer might type to the vocabulary actually used in product
 * names/specs. Distinct from Product.searchKeywords (types.ts), which tags
 * one specific product with its own aliases; this file has no notion of
 * "product," only words. Consumed by src/lib/searchProducts.ts's Tier 3.
 *
 * SYNONYM_GROUPS: symmetric — every term in a group is treated as
 * interchangeable with every other term in that group (searching any one
 * surfaces results for all of them). Use this for true naming variants of
 * the same thing (spelling variants, RU/EN pairs, colloquial short forms).
 *
 * ONE_WAY_SYNONYMS: asymmetric — key expands to its values, but not the
 * reverse. Use this when the relationship is broader-to-narrower (a
 * colloquial umbrella term that shouldn't make every narrower product
 * surface for the specific term) or when reversing would hurt precision.
 *
 * Deliberately excludes pairs that are only casually confused in speech but
 * name functionally different equipment (e.g. блендер/миксер) — conflating
 * those would recreate the exact "generic pollution" problem this system
 * exists to fix.
 */

export const SYNONYM_GROUPS: string[][] = [
  // --- Bar & coffee (CoffeeBar) ---
  ["бокал", "фужер"],
  ["закаточная машина", "баночный закатчик", "закаточный аппарат", "закатка"],
  ["кофемашина", "кофеварка"],
  ["ледогенератор", "льдогенератор", "ice maker", "генератор льда"],
  ["молочный коктейль", "милкшейк", "milkshake"],
  ["соковыжималка", "соковыжималка для цитрусовых", "citrus juicer"],
  ["стимер для молока", "капучинатор", "вспениватель молока"],

  // --- Refrigeration ---
  ["холодильник", "холодильный шкаф"],
  ["морозильный ларь", "ларь морозильный", "chest freezer"],
  ["морозильный шкаф", "морозильная камера", "upright freezer"],
  ["витрина", "холодильная витрина"],
  ["винный шкаф", "винный холодильник"],
  ["льдодробилка", "дробилка льда", "ice crusher"],

  // --- Warewashing ---
  ["посудомоечная машина", "посудомойка", "ПММ", "dishwasher"],
  ["фронтальная машина", "фронтальная посудомойка"],

  // --- Thermal ---
  ["пароконвектомат", "конвектомат", "combi steamer"],
  ["микроволновая печь", "микроволновка", "СВЧ", "microwave"],
  ["печь для пиццы", "пицца-печь", "пиццепечь", "pizza oven"],
  ["фритюрница", "фритюрный аппарат", "deep fryer"],
  ["мармит", "тепловая витрина для раздачи"],
  ["плита", "кухонная плита", "варочная плита"],
  ["гриль", "грильница"],
  ["тостер", "тостерный аппарат"],
  ["расстоечный шкаф", "расстойка", "расстоечная камера"],
  ["регенерационный шкаф", "шкаф регенерации", "regeneration oven"],
  ["сувид", "sous vide", "су вид"],

  // --- Industrial / Mechanical food-prep ---
  ["мясорубка", "мясорубочная машина", "meat grinder"],
  ["слайсер", "ломтерезка", "слайсер для нарезки", "slicer"],
  ["овощерезка", "овощерезательная машина", "vegetable cutter"],
  ["куттер", "чаша куттер", "cutter"],
  ["тестомес", "тестомесильная машина", "dough mixer"],
  ["планетарный миксер", "миксер планетарный", "planetary mixer"],
  ["картофелечистка", "машина для чистки картофеля", "potato peeler"],
  ["вакуумный упаковщик", "вакууматор", "машина вакуумной упаковки", "vacuum sealer"],
  ["пельменный аппарат", "аппарат для пельменей", "пельменница"],
  ["хлеборезка", "хлеборезательная машина", "bread slicer"],

  // --- Tableware / cutlery (the headline example in the request) ---
  ["нож для стейка", "стейковый нож", "нож для стейков", "steak knife", "steak knives"],
  ["нож для мяса", "мясной нож", "meat knife", "meat knives"],
  ["нож для рубки мяса", "тесак"],
  ["столовые приборы", "приборы", "cutlery", "flatware"],
  ["поднос", "лоток"],
  ["пиала", "чаша"],
  ["тарелка", "plate"],
  ["стакан", "тумблер", "tumbler"],
  ["рюмка", "стопка"],

  // --- Laundry ---
  ["стиральная машина", "стиралка"],
  ["сушильный автомат", "сушильная машина", "сушилка"],

  // --- Materials / general specs ---
  ["нержавейка", "нержавеющая сталь", "stainless steel"],

  // --- Noun <-> adjective derivation ---
  // The stemmer in searchProducts.ts only strips case/number endings within
  // the same word class (нож/ножи/ножа -> нож) — it can't bridge a noun to
  // a derived adjective (мясо -> мясной isn't a suffix swap, it's a
  // different word). These pairs cover the specific "X для Y" / "Y-ный Z"
  // alternation that recurs constantly across a kitchen-equipment catalog
  // ("нож для мяса" / "мясной нож", "слайсер для сыра" / "сыморезка").
  ["мясо", "мясной", "мясная", "мясные"],
  ["рыба", "рыбный", "рыбная", "рыбные"],
  ["овощи", "овощной", "овощная", "овощные"],
  ["сыр", "сырный", "сырная", "сырные"],
  ["хлеб", "хлебный", "хлебная", "хлебные"],
  ["молоко", "молочный", "молочная", "молочные"],
  ["тесто", "тестовый", "тестовая", "тестовые"],

  // --- Industry umbrella terms ---
  ["общепит", "HoReCa", "ресторанное оборудование"],
  ["инвентарь", "кухонные принадлежности", "аксессуары"],
];

export const ONE_WAY_SYNONYMS: Record<string, string[]> = {
  // Colloquial umbrella "морозилка" reasonably means either form factor —
  // but reversing (ларь/шкаф -> морозилка) would just be restating the
  // term, so this only needs to go one way.
  "морозилка": ["морозильный ларь", "морозильный шкаф"],

  // "плита" already groups with "кухонная плита"/"варочная плита" above;
  // "индукционная плита" is a narrower variant worth surfacing from the
  // broad term without the reverse (searching the broad term for the
  // specific one would be wrong the other way round only if it over-widened
  // precise induction-specific queries, which it doesn't here).
  "плита": ["индукционная плита", "induction cooker"],

  // Broad "печь" (oven) shouldn't be the reverse target of every specific
  // oven type, but a bare "печь" search benefitting from surfacing the
  // main oven categories is useful.
  "печь": ["конвекционная печь", "пароконвектомат", "печь для пиццы", "подовая печь"],

  // EN loanword for the broader raw-fish/meat processing family; narrower
  // Russian terms shouldn't all expand back out to this.
  "processing": ["обработка", "переработка"],
};
